import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useCompletedRunHandOff } from '../../../../AnalysisWorkspace/processing/useCompletedRunHandOff';
import { runViewTransition } from '../../../../viewTransition.utils';
import { batchPath } from '../../../../AnalysisWorkspace/workspacePaths.utils';
import type { ProcessingCardActions } from '../../../ProcessingCard/ProcessingCard.types';
import { useBatchProcessing } from '../../../useBatchProcessing/useBatchProcessing';
import type { ProcessingPanelController } from './useProcessingPanelController.types';

/**
 * The modal's processing frame. A completed run opens its results directly; a cancelled one stays on the card, which
 * offers "Delete now" for its files and Close. A failed batch with nothing to review also offers "Delete batch", which
 * closes the modal.
 */
export const useProcessingPanelController = (
	dealId: string,
	batchId: string,
	onClose: () => void
): ProcessingPanelController => {
	const tCapManage = useTranslationCapManage();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const processing = useBatchProcessing({ dealId, batchId });
	const { reviewBatchId, isComplete } = processing;

	useCompletedRunHandOff({
		params: { dealId, batchId: reviewBatchId },
		isComplete,
		searchParams,
		replace: false,
	});

	const actions: ProcessingCardActions = {
		onCancel: () => processing.cancel(),
		onNotify: () => {
			processing.notifyWhenComplete();
			onClose();
		},
		onRunInBackground: () => {
			showToastV2({
				title: tCapManage('dealstack_ai.ingest.processing.toast.background_title'),
				message: tCapManage('dealstack_ai.ingest.processing.toast.background_message'),
			});
			onClose();
		},
		// The modal fades out while its card morphs into the page.
		onOpenAnalysis: () => runViewTransition(() => navigate(batchPath(dealId, reviewBatchId))),
		isCancelling: processing.isCancelling,
		...(processing.canDeleteNow ? { onDeleteNow: processing.deleteNow } : {}),
		isDeletingNow: processing.isDeletingNow,
		onClose,
		...(processing.canDeleteBatch
			? { onDeleteBatch: () => processing.deleteBatch(onClose) }
			: {}),
		isDeletingBatch: processing.isDeletingBatch,
	};

	return { processing, actions };
};
